import { faTrash, faUpload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ExpenseModel } from 'api/model';
import { FileViewer } from 'components/Files/FileViewer';
import FileDropZone from 'components/Files/FileDropZone';
import TextButton from 'components/inputs/TextButton';
import Modal, { modalContentClass } from 'components/Modal';
import { ChangeEvent, FC, useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useDetailedCaseContext } from '../DetailedCaseProvider';
import { ExpenseEditorInputs } from './ExpenseEditor';
import useExpenseEditorForm from './ExpenseEditor/useExpenseEditorForm';

const MyModal = styled(Modal)<{ $wide: boolean }>`
  .${modalContentClass} {
    display: flex;
    flex-direction: column;
    gap: 10px;

    label,
    input,
    select {
      flex: 1;
      width: auto;
    }

    ${({ $wide }) =>
      $wide
        ? css`
            min-width: 900px;
            max-width: 900px;
          `
        : css`
            min-width: 500px;
            max-width: 500px;
          `}
  }
`;

const SplitLayout = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  align-items: flex-start;
`;

const PreviewColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const PreviewScroll = styled.div`
  width: 100%;
  max-height: 70vh;
  overflow: auto;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.sizes.radius.input}px;
  padding: 10px;
  cursor: grab;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
`;

const FormColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FileActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const DropZone = styled(FileDropZone)`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

interface Props {
  assignmentId: number;
  initialFile?: File;
  onCancel(): void;
}

const NewExpenseModal: FC<Props> = ({ assignmentId, initialFile, onCancel }) => {
  const {
    onSaveExpense,
    caseResponse: { response: currentCase },
  } = useDetailedCaseContext();
  const currentCaseId = currentCase?.case.caseID;

  const handleSaveExpense = useCallback(
    (expense: ExpenseModel) => {
      if (currentCaseId === undefined) return;

      const newExpense: ExpenseModel = { ...expense };
      newExpense.assignmentID = assignmentId;
      onSaveExpense(newExpense);
    },
    [assignmentId, currentCaseId, onSaveExpense]
  );

  const form = useExpenseEditorForm(undefined, handleSaveExpense, initialFile);

  const handleFilesDropped = useCallback(
    (files: File[]) => {
      const [first] = files;
      if (first) form.setSelectedFile(first);
    },
    [form]
  );

  const handleCreate = useCallback(async () => {
    await form.onSaveClick();
    onCancel();
  }, [form, onCancel]);

  // Stable blob URL for the large left-column preview.
  const [previewUrl, setPreviewUrl] = useState<string>();
  useEffect(() => {
    if (!form.selectedFile) {
      setPreviewUrl(undefined);
      return undefined;
    }
    const url = URL.createObjectURL(form.selectedFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [form.selectedFile]);

  const hasFile = !!form.selectedFile && !!previewUrl;

  // Click-and-drag panning on the preview, since receipts often need both
  // horizontal and vertical scrolling and the wheel/scrollbar UX is fiddly.
  const previewScrollRef = useRef<HTMLDivElement>(null);
  const handlePreviewPointerDown = useCallback(
    (eve: React.PointerEvent<HTMLDivElement>) => {
      // Ignore non-primary buttons so right-click / context menu still works
      if (eve.button !== 0) return;
      const el = previewScrollRef.current;
      if (!el) return;
      // Prevent native HTML5 image-drag (which would otherwise hijack the
      // pointer and trigger our outer FileDropZone or browser file drop)
      eve.preventDefault();

      const startX = eve.clientX;
      const startY = eve.clientY;
      const startScrollLeft = el.scrollLeft;
      const startScrollTop = el.scrollTop;

      const handleMove = (moveEve: PointerEvent) => {
        el.scrollLeft = startScrollLeft - (moveEve.clientX - startX);
        el.scrollTop = startScrollTop - (moveEve.clientY - startY);
      };
      const handleUp = () => {
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleUp);
        window.removeEventListener('pointercancel', handleUp);
      };
      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
      window.addEventListener('pointercancel', handleUp);
    },
    []
  );

  const replaceFileInputRef = useRef<HTMLInputElement>(null);
  const handleReplaceFilePicked = useCallback(
    (eve: ChangeEvent<HTMLInputElement>) => {
      const input = eve.target;
      const file = input.files?.item(0);
      if (file) form.setSelectedFile(file);
      // Reset value so picking the same file again still triggers onChange
      input.value = '';
    },
    [form]
  );

  return (
    <MyModal
      $wide={hasFile}
      buttons={[
        { label: 'Skapa', onClick: handleCreate },
        { label: 'Avbryt', onClick: onCancel },
      ]}
      onClose={onCancel}
      title="Ny utgift"
    >
      <DropZone
        onFilesDropped={handleFilesDropped}
        overlayLabel="Släpp filen för att bifoga som kvitto"
        padded
      >
        {hasFile ? (
          <SplitLayout>
            <PreviewColumn>
              <PreviewScroll
                ref={previewScrollRef}
                onPointerDown={handlePreviewPointerDown}
              >
                <FileViewer
                  imageB64Url={previewUrl!}
                  mimeType={form.selectedFile!.type}
                  maxHeight={600}
                  browsable
                />
              </PreviewScroll>
              <FileActions>
                <TextButton
                  onClick={() => replaceFileInputRef.current?.click()}
                >
                  <FontAwesomeIcon icon={faUpload} /> Byt fil
                </TextButton>
                <TextButton onClick={() => form.setSelectedFile(null)}>
                  <FontAwesomeIcon icon={faTrash} /> Ta bort fil
                </TextButton>
              </FileActions>
              <HiddenFileInput
                ref={replaceFileInputRef}
                type="file"
                accept="image/*,application/pdf"
                onChange={handleReplaceFilePicked}
              />
            </PreviewColumn>
            <FormColumn>
              <ExpenseEditorInputs expenseEditorForm={form} hideFileField />
            </FormColumn>
          </SplitLayout>
        ) : (
          <ExpenseEditorInputs expenseEditorForm={form} />
        )}
      </DropZone>
    </MyModal>
  );
};

export default NewExpenseModal;
